import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Tag, message, Tooltip, Progress, Input, Select, DatePicker, Space, Popconfirm, Card } from "antd";
import { PlusOutlined, FileTextOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import pushBroadcastApi from "../../../api/push-broadcast-api";
import PushStatCards from "./push-stat-cards";

const AUDIENCE_TAG = {
    all: { color: "blue", label: "Tất cả" },
    roles: { color: "green", label: "Theo vai trò" },
    criteria: { color: "purple", label: "Bộ lọc" },
    single: { color: "default", label: "Cá nhân" },
    ids: { color: "orange", label: "Danh sách ID" },
};

const STATUS_TAG = {
    sent: { color: "green", label: "Đã gửi" },
    scheduled: { color: "blue", label: "Đã lên lịch" },
    sending: { color: "processing", label: "Đang gửi" },
    cancelled: { color: "default", label: "Đã hủy" },
    failed: { color: "red", label: "Gửi lỗi" },
};

const AUDIENCE_FILTER_OPTIONS = Object.entries(AUDIENCE_TAG).map(([value, t]) => ({ value, label: t.label }));

function PushBroadcastList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [refreshKey, setRefreshKey] = useState(0);

    // Bộ lọc: status đẩy lên BE (do phân trang server-side); còn lại lọc client trên trang hiện tại.
    const [statusFilter, setStatusFilter] = useState(undefined);
    const [search, setSearch] = useState("");
    const [audienceFilter, setAudienceFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);

    useEffect(() => {
        fetchBroadcasts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current, statusFilter]);

    const fetchBroadcasts = async () => {
        try {
            setLoading(true);
            const offset = (pagination.current - 1) * pagination.pageSize;
            const { data } = await pushBroadcastApi.listBroadcasts({ limit: pagination.pageSize, offset, status: statusFilter });
            setItems(data.data.items || []);
            setPagination((prev) => ({ ...prev, total: data.data.total || 0 }));
        } catch {
            message.error("Không thể tải danh sách push notification");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (id) => {
        try {
            await pushBroadcastApi.cancelBroadcast(id);
            message.success("Đã hủy lịch gửi");
            fetchBroadcasts();
            setRefreshKey((k) => k + 1);
        } catch (err) {
            message.error(err?.response?.data?.message || "Hủy thất bại");
        }
    };

    const handleDelete = async (id) => {
        try {
            await pushBroadcastApi.deleteBroadcast(id);
            message.success("Đã xóa");
            fetchBroadcasts();
            setRefreshKey((k) => k + 1);
        } catch (err) {
            message.error(err?.response?.data?.message || "Xóa thất bại");
        }
    };

    const filteredItems = useMemo(() => {
        return items.filter((r) => {
            if (search && !String(r.title || "").toLowerCase().includes(search.toLowerCase())) return false;
            if (audienceFilter && r.audienceType !== audienceFilter) return false;
            if (dateRange && dateRange[0] && dateRange[1]) {
                const ts = Number(r.createdAt);
                if (ts < dateRange[0].startOf("day").valueOf() || ts > dateRange[1].endOf("day").valueOf()) return false;
            }
            return true;
        });
    }, [items, search, audienceFilter, dateRange]);

    const columns = [
        {
            title: "Tiêu đề / Nội dung",
            key: "content",
            ellipsis: true,
            render: (_, record) => (
                <div style={{ maxWidth: 260 }}>
                    <a style={{ fontWeight: 600, display: "block", marginBottom: 2 }} onClick={() => navigate(`/push/${record.broadcastId}`)}>
                        {record.title}
                    </a>
                    <div style={{ fontSize: 12, color: "#8c8c8c", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {record.message}
                    </div>
                    {record.createdBy && <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>👤 {record.createdBy}</div>}
                </div>
            ),
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            width: 130,
            render: (status, record) => {
                const t = STATUS_TAG[status] || STATUS_TAG.sent;
                return (
                    <div>
                        <Tag color={t.color}>{t.label}</Tag>
                        {status === "scheduled" && record.scheduledAt && (
                            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
                                {dayjs(Number(record.scheduledAt)).format("DD/MM HH:mm")}
                            </div>
                        )}
                        {status === "failed" && record.lastError && (
                            <Tooltip title={record.lastError}>
                                <div style={{ fontSize: 11, color: "#cf1322", marginTop: 2 }} className="truncate">
                                    {record.lastError}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Đối tượng",
            dataIndex: "audienceType",
            width: 110,
            render: (type) => {
                const t = AUDIENCE_TAG[type] || { color: "default", label: type };
                return <Tag color={t.color}>{t.label}</Tag>;
            },
        },
        {
            // Hai đường gửi cho kết quả thống kê khác hẳn nhau: topic không đo được
            // lượt bấm nên "Tỷ lệ mở" luôn là 0. Không hiện cột này thì nhìn lại lịch
            // sử sẽ tưởng chiến dịch flop, trong khi chỉ là không có số liệu.
            title: "Cách gửi",
            dataIndex: "sendMode",
            width: 100,
            render: (mode) =>
                mode === "topic" ? <Tag color="volcano">Topic</Tag> : <Tag>Lần lượt</Tag>,
        },
        { title: "Target", dataIndex: "targetUserCount", width: 80, align: "right", render: (n) => <span style={{ fontWeight: 500 }}>{n?.toLocaleString() ?? "-"}</span> },
        {
            title: "Đã gửi",
            key: "sent",
            width: 80,
            align: "right",
            render: (_, record) => {
                const n = record.stats?.notificationCount;
                return n != null ? <span style={{ color: n > 0 ? "#1677ff" : "#d9d9d9" }}>{n.toLocaleString()}</span> : "-";
            },
        },
        {
            title: "Tỷ lệ mở",
            key: "openRate",
            width: 110,
            render: (_, record) => {
                const sent = record.stats?.notificationCount;
                const opened = record.stats?.distinctUsersOpened;
                // Gửi bằng topic thì push không mang notification_id riêng từng người
                // ⇒ không có dữ liệu lượt bấm. Vẽ thanh 0% là bịa ra một con số sai;
                // nói thẳng "không đo được" mới đúng.
                if (record.sendMode === "topic") {
                    return (
                        <Tooltip title="Gửi bằng Topic nên không đo được lượt bấm của từng người">
                            <span style={{ color: "#d9d9d9" }}>không đo</span>
                        </Tooltip>
                    );
                }
                if (!sent) return <span style={{ color: "#d9d9d9" }}>-</span>;
                const pct = Math.round((opened / sent) * 100);
                return (
                    <Tooltip title={`${opened?.toLocaleString()} / ${sent?.toLocaleString()} users`}>
                        <div style={{ minWidth: 90 }}>
                            <Progress percent={pct} size="small" strokeColor={pct >= 20 ? "#52c41a" : pct >= 10 ? "#faad14" : "#ff4d4f"} format={(p) => <span style={{ fontSize: 11 }}>{p}%</span>} />
                        </div>
                    </Tooltip>
                );
            },
        },
        { title: "Thời gian", dataIndex: "createdAt", width: 130, render: (ts) => (ts ? dayjs(Number(ts)).format("DD/MM/YY HH:mm") : "-") },
        {
            title: "",
            width: 210,
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" onClick={() => navigate(`/push/${record.broadcastId}`)}>Chi tiết</Button>
                    <Button size="small" type="link" onClick={() => navigate(`/push/new?from=${record.broadcastId}`)}>Gửi lại</Button>
                    {record.status === "scheduled" ? (
                        <Popconfirm title="Hủy lịch gửi này?" onConfirm={() => handleCancel(record.broadcastId)} okText="Hủy lịch" cancelText="Thôi">
                            <Button size="small" danger type="link">Hủy</Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm title="Xóa chiến dịch này?" onConfirm={() => handleDelete(record.broadcastId)} okText="Xóa" cancelText="Thôi">
                            <Button size="small" danger type="link">Xóa</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="flex-1 p-6 overflow-auto" style={{ background: "rgb(var(--surface-sunken))" }}>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
                <div>
                    <h1 className="text-xl font-semibold m-0">Push Notification</h1>
                    <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 2 }}>Quản lý chiến dịch thông báo đẩy</div>
                </div>
                <Space>
                    <Button icon={<FileTextOutlined />} onClick={() => navigate("/push/templates")}>Quản lý mẫu</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/push/new")}>Tạo chiến dịch</Button>
                </Space>
            </div>

            <PushStatCards refreshKey={refreshKey} />

            <Card bodyStyle={{ padding: 12 }} style={{ borderRadius: 12 }} className="mb-3">
                <Space wrap>
                    <Input.Search placeholder="Tìm theo tiêu đề" allowClear style={{ width: 220 }} onChange={(e) => setSearch(e.target.value)} />
                    <Select placeholder="Trạng thái" allowClear style={{ width: 150 }} value={statusFilter}
                        onChange={(v) => { setStatusFilter(v); setPagination((p) => ({ ...p, current: 1 })); }}
                        options={Object.entries(STATUS_TAG).filter(([k]) => k !== "sending").map(([value, t]) => ({ value, label: t.label }))} />
                    <Select placeholder="Đối tượng" allowClear style={{ width: 150 }} value={audienceFilter} onChange={setAudienceFilter} options={AUDIENCE_FILTER_OPTIONS} />
                    <DatePicker.RangePicker onChange={setDateRange} format="DD/MM/YYYY" />
                    <Tooltip title="Tải lại">
                        <Button icon={<ReloadOutlined />} onClick={() => { fetchBroadcasts(); setRefreshKey((k) => k + 1); }} />
                    </Tooltip>
                </Space>
            </Card>

            <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: "hidden" }}>
            <Table
                rowKey="broadcastId"
                dataSource={filteredItems}
                columns={columns}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showTotal: (total) => `Tổng: ${total}`,
                    onChange: (page) => setPagination((prev) => ({ ...prev, current: page })),
                }}
                scroll={{ x: "max-content" }}
            />
            </Card>
        </div>
    );
}

export default PushBroadcastList;
